import type { User } from "firebase/auth";

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

export interface PlatformOrganizationRenewal {
  id: string;
  name: string;
  subscriptionStatus: string | null;
  planId: string | null;
  planName: string;
  monthlyUsd: number | null;
  currentPeriodEnd: string | null;
  paymentMethod?: string | null;
  countsTowardRevenue?: boolean;
  intelligence?: {
    capacityId: string;
    trialing: boolean;
    creditsAllowance: number;
    creditsUsed: number;
    periodEnd: string | null;
    monthlyUsd: number | null;
    hasStripeItem: boolean;
  } | null;
}

export interface PlatformIntelligenceMetrics {
  active: number;
  trialing: number;
  total: number;
  monthlyUsd: number;
  byCapacity: Record<string, number>;
}

export interface PlatformOrganizationMetricsResponse {
  totalActivePlans: number;
  payingActive: number;
  inTrial: number;
  expiredPaying?: number;
  expiredTrialing?: number;
  excludedTestOrgs?: number;
  premiumProfessional: number;
  totalMonthlyUsd: number;
  monthlyUsdExcluded: number;
  byPlan: Record<string, number>;
  renewals: PlatformOrganizationRenewal[];
  intelligence?: PlatformIntelligenceMetrics;
}

export const fetchPlatformOrganizationMetrics = async (
  user: User
): Promise<PlatformOrganizationMetricsResponse> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/organization-metrics`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
  return data as PlatformOrganizationMetricsResponse;
};
